import {
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
} from "react";
import { useProjectMutation } from "../migration/project-mutation";
import { PhasePath } from "./PhasePath";
import {
  productionPhases,
  unblockTarget,
  type PhaseId,
  type PhaseStep,
  type ProductionPhase,
} from "./production-phases";
import "./workspace.css";

export type WorkflowStatus = {
  title?: string;
  detail?: string;
  tail: string;
  live?: boolean;
};
export type ProductionStep = {
  id: string;
  label: string;
  hint: string;
  state: "current" | "complete" | "blocked" | "upcoming" | "skipped";
};
export interface ProductionShellProps {
  projectId: string;
  title: string;
  artist: string;
  style: string;
  autopilot: boolean;
  completed: boolean;
  kpis: string[];
  steps: PhaseStep[];
  /** Whether each phase is finished (#101); the shell groups the steps itself. */
  phaseDone: Record<PhaseId, boolean>;
  /** The first line of the phase-complete card, e.g. "18 shots, all pictures approved." */
  phaseSummaries?: Partial<Record<"intake" | "storyboard", string>>;
  progress: number;
  status: WorkflowStatus;
  navigate: (step: string) => void;
  rename: (title: string) => Promise<void>;
  setCompleted: (completed: boolean) => Promise<void>;
  remove: () => Promise<void>;
  /** Iris leads: a calm progress line replaces the step buttons; she navigates for the maker. */
  guided?: boolean;
}

const saveStates = new Map<string, string>();
const saveListeners = new Set<() => void>();
export function setProductionSaveStatus(projectId: string, text: string) {
  saveStates.set(projectId, text);
  for (const listener of saveListeners) listener();
}
const subscribe = (listener: () => void) => {
  saveListeners.add(listener);
  return () => {
    saveListeners.delete(listener);
  };
};

const PHASE_CARDS = {
  intake: { title: "Song ready", next: "Next: Storyboard — choose your cast & looks.", button: "Open Storyboard" },
  storyboard: {
    title: "Storyboard complete",
    next: "Next: Release — make a preview, then your film, cover and shorts. Nothing is charged until you confirm.",
    button: "Open Release",
  },
} as const;

/** At the end of a finished phase: what is done and where to go next. Not a modal; it blocks nothing. */
function PhaseCard({ phases, summaries, navigate }: {
  phases: ProductionPhase[];
  summaries?: ProductionShellProps["phaseSummaries"];
  navigate: (step: string) => void;
}) {
  const index = phases.findIndex((phase) => phase.state === "current");
  const phase = phases[index], next = phases[index + 1];
  if (!phase || !next || phase.id === "release" || !phase.done) return null;
  const last = phase.steps[phase.steps.length - 1];
  if (!last || (last.state !== "current" && last.state !== "blocked")) return null;
  const card = PHASE_CARDS[phase.id];
  const summary = summaries?.[phase.id];
  return (
    <section className="phase-complete-card" aria-label={card.title}>
      <p>
        <b>✓ {card.title}</b>
        {summary ? ` · ${summary}` : ""}
      </p>
      <p>{card.next}</p>
      <button type="button" className="btn primary small" onClick={() => navigate(next.target)}>
        {card.button}
      </button>
    </section>
  );
}

export function ProductionShell(props: ProductionShellProps) {
  const mutation = useProjectMutation("production-details", props.projectId);
  const saveState = useSyncExternalStore(
    subscribe,
    () => saveStates.get(props.projectId) || "Saved on server",
  );
  const [dialog, setDialog] = useState<"rename" | "delete" | null>(null);
  const [title, setTitle] = useState(props.title);
  const modal = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const heading = useId(),
    field = useId();
  useEffect(() => {
    if (!dialog) return;
    modal.current?.showModal();
    if (dialog === "rename") {
      input.current?.focus();
      input.current?.select();
    }
    return () => modal.current?.close();
  }, [dialog]);
  const close = () => {
    if (!mutation.pending) setDialog(null);
  };
  const submit = async () => {
    if (!dialog || mutation.pending || (dialog === "rename" && !title.trim()))
      return;
    const saved = await mutation.run(() =>
      dialog === "rename" ? props.rename(title.trim()) : props.remove(),
    );
    if (saved) setDialog(null);
  };
  const phases = productionPhases(props.steps, props.phaseDone);
  const open = props.steps.find((step) => step.state === "blocked");
  const unblock = open ? unblockTarget(props.steps, open.id) : null;
  const workflowStyle = {
    "--workflow-progress": `${props.progress}%`,
    "--workflow-color": props.steps.some((step) => step.state === "blocked")
      ? "var(--amber)"
      : "var(--violet)",
    // The step row shows only the open phase's steps (#101).
    "--workflow-step-count": phases.find((phase) => phase.state === "current")?.steps.length ?? props.steps.length,
  } as CSSProperties;
  return (
    <>
      <div className="workspace-heading">
        <div className="workspace-identity">
          <div className="eyebrow">
            {props.autopilot ? "First-cut route" : "Guided route"}
          </div>
          <h1>{props.title}</h1>
          <p>
            {props.artist} · {props.style}
          </p>
          <div className="workspace-kpis">
            {props.kpis.map((value) => (
              <span key={value}>{value}</span>
            ))}
          </div>
        </div>
        <div className="workspace-actions">
          <span className="save-state" id="lyricSaveState" role="status">
            {saveState}
          </span>
          <button
            className="btn small danger"
            id="lyricDeleteVisible"
            type="button"
            disabled={mutation.pending}
            onClick={() => setDialog("delete")}
          >
            Delete production
          </button>
          <details className="workspace-menu">
            <summary className="btn">••• More</summary>
            <div className="workspace-popover">
              <button
                id="lyricRename"
                aria-label="Rename production"
                type="button"
                disabled={mutation.pending}
                onClick={() => {
                  setTitle(props.title);
                  setDialog("rename");
                }}
              >
                Rename production
              </button>
              <button
                id="lyricComplete"
                aria-label={
                  props.completed ? "Reopen production" : "Mark as completed"
                }
                type="button"
                disabled={mutation.pending}
                onClick={() =>
                  void mutation.run(() => props.setCompleted(!props.completed))
                }
              >
                {props.completed ? "Reopen production" : "Mark as completed"}
              </button>
            </div>
          </details>
        </div>
      </div>
      <div className="lyric-workflow" style={workflowStyle}>
        <div className="lyric-workflow-track">
          <span />
        </div>
        <PhasePath phases={phases} guided={!!props.guided} navigate={props.navigate} progress={props.progress} />
        <div className="lyric-workflow-status">
          {/* Only the status text is live; the button beside it is not re-announced on every refresh. */}
          <div className="lyric-workflow-live" role="status">
            <span>
              {props.status.live && <i className="workflow-live-dot" />}
              {props.status.title && <b>{props.status.title}</b>}
              {props.status.title && props.status.detail ? " · " : ""}
              {props.status.detail}
            </span>
            <span>{props.status.tail}</span>
          </div>
          {unblock && (
            <button type="button" className="btn small" onClick={() => props.navigate(unblock.id)}>
              Go to {unblock.label}
            </button>
          )}
        </div>
      </div>
      <PhaseCard phases={phases} summaries={props.phaseSummaries} navigate={props.navigate} />
      {mutation.error && !dialog && (
        <div className="notice warn" role="alert">
          {mutation.error}
        </div>
      )}
      {dialog && (
        <dialog
          className="production-manage-dialog"
          ref={modal}
          aria-labelledby={heading}
          onCancel={(event) => {
            event.preventDefault();
            close();
          }}
        >
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <h2 id={heading}>
              {dialog === "rename" ? "Rename production" : "Delete production?"}
            </h2>
            {dialog === "rename" ? (
              <>
                <div className="field">
                  <label htmlFor={field}>Production title</label>
                  <input
                    ref={input}
                    id={field}
                    maxLength={160}
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    disabled={mutation.pending}
                    autoComplete="off"
                  />
                </div>
                <div className="notice">
                  Only the project name and future title cards change.
                  Storyboard approval, generated images, motion and existing
                  renders remain untouched.
                </div>
              </>
            ) : (
              <>
                <p>
                  Permanently delete “{props.title}”, its storyboard and
                  generated production files? This cannot be undone.
                </p>
                <div className="notice">
                  Your reusable Cast &amp; Assets library stays available and is
                  not deleted.
                </div>
              </>
            )}
            {mutation.error && (
              <div className="notice warn" role="alert">
                {mutation.error}
              </div>
            )}
            <div className="dialog-actions">
              <button
                type="button"
                className="btn"
                disabled={mutation.pending}
                onClick={close}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`btn ${dialog === "delete" ? "danger" : "primary"}`}
                disabled={
                  mutation.pending || (dialog === "rename" && !title.trim())
                }
              >
                {mutation.pending
                  ? "Saving…"
                  : dialog === "rename"
                    ? "Save title"
                    : "Delete permanently"}
              </button>
            </div>
          </form>
        </dialog>
      )}
    </>
  );
}
